import React, { useRef } from 'react';
import { Platform, type StyleProp, type View, type ViewStyle } from 'react-native';

import { Column } from 'app/shopify/components/layout';

import { RadioButton, type RadioButtonSize, type RadioButtonState } from './RadioButton';

export interface RadioButtonListOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioButtonListProps {
  options: readonly RadioButtonListOption[];
  selectedValue?: string;
  size?: RadioButtonSize;
  state?: RadioButtonState;
  disabled?: boolean;
  accessibilityLabel?: string;
  style?: StyleProp<ViewStyle>;
  onChange?: (value: string) => void;
}

export function RadioButtonList({
  options,
  selectedValue,
  size = 'small',
  state = 'enabled',
  disabled = false,
  accessibilityLabel,
  style,
  onChange,
}: RadioButtonListProps) {
  const refs = useRef<Record<string, View | null>>({});
  const isDisabled = disabled || state === 'disabled';
  const enabledOptions = options.filter((option) => !option.disabled);
  const tabStop = enabledOptions.find((option) => option.value === selectedValue) ?? enabledOptions[0];

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>, value: string) {
    if (isDisabled || event.altKey || event.ctrlKey || event.metaKey) return;
    const index = enabledOptions.findIndex((option) => option.value === value);
    if (index === -1) return;
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (index + 1) % enabledOptions.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (index - 1 + enabledOptions.length) % enabledOptions.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = enabledOptions.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = enabledOptions[nextIndex];
    if (!next) return;
    refs.current[next.value]?.focus();
    if (next.value !== selectedValue) onChange?.(next.value);
  }

  return (
    <Column
      accessibilityRole="radiogroup"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: isDisabled }}
      alignSelf="flex-start"
      maxWidth="100%"
      paddingVertical="spacing.general.x4"
      gap="spacing.general.x4"
      style={style}
    >
      {options.map((option) => (
        <RadioButton
          key={option.value}
          ref={(node) => {
            refs.current[option.value] = node;
          }}
          size={size}
          state={state}
          disabled={isDisabled || option.disabled}
          label={option.label}
          description={option.description}
          showDescription={Boolean(option.description)}
          selected={selectedValue === option.value}
          style={{ alignSelf: 'stretch' }}
          {...(Platform.OS === 'web'
            ? {
                tabIndex: !isDisabled && option.value === tabStop?.value ? 0 : -1,
                onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => handleKeyDown(event, option.value),
              }
            : {})}
          onPress={() => onChange?.(option.value)}
        />
      ))}
    </Column>
  );
}
